import React, { useEffect, useState, useRef, useContext } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useChat } from './ChatContext';
import { IconButton, Badge, Tooltip } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth, UserContextType } from '../auth';

// --- STYLES ---

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ChatWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(20, 20, 35, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  color: white;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.div`
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h3`
  margin: 0;
  font-weight: 600;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const MessagesArea = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const UsersArea = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const UserItem = styled.div`
  padding: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:last-child {
    border-bottom: none;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
`;

const MessageBubble = styled.div<{ isOwn?: boolean }>`
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${props => props.isOwn ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(255, 255, 255, 0.1)'};
  color: #fff;
  align-self: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
  border-bottom-${props => props.isOwn ? 'right' : 'left'}-radius: 2px;
  animation: ${fadeIn} 0.3s ease-out;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const MessageUser = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 4px;
`;

const MessageText = styled.div`
  font-size: 0.9rem;
  line-height: 1.4;
  word-wrap: break-word;
`;

const InputArea = styled.div`
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  gap: 10px;
  align-items: center;
`;

const StyledInput = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px 20px;
  border-radius: 30px;
  color: #fff;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.3);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  font-size: 0.9rem;
`;

interface ModernChatProps {
  onClose?: () => void;
}

const ModernChat: React.FC<ModernChatProps> = ({ onClose }) => {
  const { messages, onlineUsers, sendMessage } = useChat();
  const { getUserName } = useContext<UserContextType>(useAuth);

  const [inputText, setInputText] = useState("");
  const [view, setView] = useState<'chat' | 'users'>('chat');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const myName = getUserName ? getUserName() : "Anonymous";

  useEffect(() => {
    if (view === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, view]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    sendMessage(inputText);
    setInputText("");
  };

  return (
    <ChatWrapper>
      <Header>
        <Title>
          {view === 'chat' ? 'Live Chat' : 'Online Users'}
        </Title>
        <div style={{ display: 'flex', gap: '5px' }}>
          <Tooltip title="Chat">
            <IconButton onClick={() => setView('chat')} sx={{ color: view === 'chat' ? 'white' : 'rgba(255,255,255,0.5)' }}>
              <Badge color="secondary" variant="dot" invisible={messages.length === 0}>
                <ChatIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Online Users">
            <IconButton onClick={() => setView('users')} sx={{ color: view === 'users' ? 'white' : 'rgba(255,255,255,0.5)' }}>
              <Badge badgeContent={onlineUsers.length} color="primary">
                <PeopleIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          {onClose && (
            <Tooltip title="Close">
              <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </Header>

      <ContentArea>
        {view === 'chat' ? (
          <>
            <MessagesArea>
              {messages.length === 0 && <EmptyState>No messages yet. Say hello!</EmptyState>}
              {messages.map((msg) => (
                <MessageBubble key={msg.id} isOwn={msg.isOwn || msg.user === myName}>
                  {(!msg.isOwn && msg.user !== myName) && <MessageUser>{msg.user}</MessageUser>}
                  <MessageText>{msg.text}</MessageText>
                </MessageBubble>
              ))}
              <div ref={messagesEndRef} />
            </MessagesArea>
            <InputArea>
              <StyledInput
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type a message..."
              />
              <SendButton onClick={handleSend}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </SendButton>
            </InputArea>
          </>
        ) : (
          <UsersArea>
            {onlineUsers.length === 0 && <EmptyState>No users online</EmptyState>}
            {onlineUsers.map((user, idx) => (
              <UserItem key={idx}>
                <UserAvatar>{user.charAt(0).toUpperCase()}</UserAvatar>
                <span>{user}</span>
              </UserItem>
            ))}
          </UsersArea>
        )}
      </ContentArea>
    </ChatWrapper>
  );
};

export default ModernChat;

